// The seeded `steps` markdown, turned into the numbered how-to the guided path
// shows inside an open step.
//
// Renders `**bold**` and `[label](url)` the same way the item modal's
// `renderSimpleMarkdown` does — `data-internal-link="true"` anchors that the
// panel intercepts (capture-phase, like the modal) and routes through
// `onNavigate` instead of a full-page load. Lines that came from the seed
// already numbered keep their markdown intact; only the prose fallback below
// (a `steps` string authored as one paragraph) falls back to plain text,
// because splitting on sentence/clause punctuation isn't markdown-aware.
//
// Two shapes arrive from the seed and both have to become a numbered list:
//
//   authored as a list   "1. Go to Suppliers\n2. Click Upload"
//   authored as prose    "Go to Suppliers, click the Upload dropdown, then
//                         select Upload Suppliers to import your spreadsheet."
//
// The second is the common one, and rendering it whole gave a "how-to" that was
// one paragraph wearing a ① — so a single line gets split on its own sentence
// and clause boundaries. That is a heuristic over copy nobody wrote for this
// purpose; the durable fix is authoring `steps` as one instruction per line in
// the seed, after which the splitting below never fires.

const LIST_MARKER = /^\s*(?:\d+[.)]|[-*•])\s+/;

// Words that make a clause the NEXT step ("…, then select Upload") — stripped,
// because the numbering already says "next".
const SEQUENCER = /^(?:and then|then|and|next|finally|after that)\s+/i;

// Words that make a clause a continuation of the one before it ("Download the
// template, or export from your customs software"). One of these anywhere in
// the split means the commas were punctuation, not step boundaries.
const CONTINUATION = /^(?:or|but|nor|which|that|because|so|including)\b/i;

// A clause shorter than this is almost always an item in a list rather than an
// instruction — "CN code, quantity, origin" must not become three steps.
const MIN_CLAUSE_WORDS = 3;

/** `**bold**` → bold, `[label](url)` → label. Used only for word counts and
 *  the prose-splitting fallback, where markdown can't safely survive a split
 *  on punctuation. */
const toPlainText = (line) =>
  line
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

// `**bold**` → <strong>, `[label](url)` → an internal-link anchor the panel
// intercepts, same convention as `renderSimpleMarkdown`. HTML-escaped first
// so authored text can't inject markup.
const toHtml = (line) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return line
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) => {
      const isInternal = url.startsWith('/') || (origin && url.startsWith(origin));
      const path = isInternal ? (url.startsWith('/') ? url : url.replace(origin, '')) : url;
      return isInternal
        ? `<a href="${path}" data-internal-link="true" class="text-primaryColor underline font-medium">${label}</a>`
        : `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primaryColor underline font-medium">${label}</a>`;
    })
    .replace(/\s+/g, ' ')
    .trim();
};

const wordCount = (text) => text.split(/\s+/).filter(Boolean).length;

const tidy = (clause) => {
  const stripped = clause
    .replace(SEQUENCER, '')
    .replace(/[.,;]+$/, '')
    .trim();
  return stripped ? stripped[0].toUpperCase() + stripped.slice(1) : '';
};

// Same trim-and-capitalize as `tidy`, minus `SEQUENCER` (that only matters
// for prose clauses) — used on lines the author already numbered, so their
// markdown must survive untouched.
const tidyMarkdown = (line) => {
  const stripped = line.replace(/[.,;]+$/, '').trim();
  return stripped ? stripped[0].toUpperCase() + stripped.slice(1) : '';
};

/**
 * Prose → steps. Sentences first; failing that, comma/semicolon clauses, but
 * only when every one of them reads like an instruction.
 */
const splitProse = (line) => {
  // No lookbehind: Safari only got it in 16.4, and a SyntaxError here would
  // take the whole module down rather than degrade.
  const sentences = (line.match(/[^.!?]+[.!?]*/g) || []).map((s) => s.trim()).filter(Boolean);
  if (sentences.length > 1) return sentences;

  const clauses = line
    .split(/\s*[;,]\s+/)
    .map((c) => c.trim())
    .filter(Boolean);
  const splittable =
    clauses.length > 1 &&
    clauses.every((c) => wordCount(c) >= MIN_CLAUSE_WORDS) &&
    !clauses.some((c) => CONTINUATION.test(c));
  if (splittable) return clauses;

  const chained = line
    .split(/\s+then\s+/i)
    .map((c) => c.trim())
    .filter(Boolean);
  if (chained.length > 1) return chained;

  return [line];
};

/**
 * @param {string|undefined} steps  the item's `steps` markdown
 * @param {number} limit            how many lines the card has room for
 * @returns {string[]}              HTML instructions (safe to
 *                                  `dangerouslySetInnerHTML`), never more than `limit`
 */
export const parseStepHowTo = (steps, limit = 3) => {
  if (!steps || typeof steps !== 'string') return [];

  const rawLines = steps
    .split('\n')
    .map((line) => line.replace(LIST_MARKER, '').trim())
    .filter(Boolean);

  // A seed that already numbered its steps has said where the boundaries
  // are — keep each line's markdown intact rather than second-guessing it.
  if (rawLines.length > 1) {
    return rawLines.map(tidyMarkdown).map(toHtml).filter(Boolean).slice(0, limit);
  }

  // Prose fallback: splitting on sentence/clause punctuation isn't
  // markdown-aware, so this path works in plain text and any `**bold**` or
  // `[label](url)` in the source is lost. Durable fix, per the module intro,
  // is authoring `steps` as one instruction per line.
  const plainLine = toPlainText(rawLines[0] ?? '');
  const parts = splitProse(plainLine);
  return parts.map(tidy).map(toHtml).filter(Boolean).slice(0, limit);
};

export default parseStepHowTo;
